import { Button, message, Input, DatePicker, Select, Space } from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import { SearchOutlined, WalletOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import CustomTable from "../../components/Table/CustomTable";
import { getUserRole } from "../../utils/auth";
import EmployeeCommissionDrawer from "./EmployeeCommissionDrawer";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

export default function EmployeeSalesReport() {
  const [commissions, setCommissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    employeeId: null,
    search: "",
    month: null,
  });
  const [userRole, setUserRole] = useState("");
  const [summary, setSummary] = useState({
    totalCommissions: 0,
    totalCommissionPaid: "0.00",
  });
  const [commissionSummary, setCommissionSummary] = useState({
    commissionCount: 0,
    totalCommissionPaid: "0.00",
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    getEmployees();
  }, []);

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value || null,
    }));
  };

  const handleMonthChange = (month) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      month: month ? month.format("YYYY-MM") : null,
    }));
  };

  const fetchCommissionSummary = async (employeeId, month) => {
    if (!employeeId || !month) {
      setCommissionSummary({
        commissionCount: 0,
        totalCommissionPaid: "0.00",
      });
      return;
    }

    try {
      const response = await API.get("/employee-commissions/paginated", {
        params: {
          employeeId,
          month: month,
          status: 'paid',
          page: 1,
          limit: 1000, // Get all paid commissions
        },
      });
      
      const commissions = response?.data?.commissions || [];
      const commissionCount = commissions.length;
      const totalCommissionPaid = commissions.reduce((sum, commission) => sum + (commission.amount || 0), 0);
      
      setCommissionSummary({
        commissionCount,
        totalCommissionPaid: totalCommissionPaid.toFixed(2),
      });
    } catch (err) {
      console.error("Error fetching commission summary:", err);
      setCommissionSummary({
        commissionCount: 0,
        totalCommissionPaid: "0.00",
      });
    }
  };

  const getSales = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { employeeId, search, month } = filters;
      if (!employeeId || !month) {
        setCommissions([]);
        setSummary({
          totalCommissions: 0,
          totalCommissionPaid: "0.00",
        });
        setPaginationInfo({
          current: page,
          pageSize,
          total: 0,
        });
        return;
      }
      
      // Generate date range from month
      const monthStart = dayjs(month).startOf('month');
      const monthEnd = dayjs(month).endOf('month');
      const fromDate = monthStart.format("YYYY-MM-DD");
      const toDate = monthEnd.format("YYYY-MM-DD");
      
      // Get sales data for the employee
      console.log("Fetching sales for employee:", { employeeId, month, fromDate, toDate, page, pageSize, search });
      
      const response = await API.get("/sales/employee", {
        params: {
          employeeId,
          fromDate,
          toDate,
          page,
          limit: pageSize,
          search,
        },
      });

      console.log("Sales API Response:", response?.data);
      
      const salesData = Array.isArray(response?.data?.sales)
        ? response.data.sales
        : [];

      // Get existing commission records to check which sales already have commissions
      const commissionResponse = await API.get("/employee-commissions/paginated", {
        params: {
          employeeId,
          month: month,
          page: 1,
          limit: 1000, // Get all commissions to check against sales
        },
      });

      const existingCommissions = Array.isArray(commissionResponse?.data?.commissions)
        ? commissionResponse.data.commissions
        : [];

      // Create a map of sales with commission details (ID -> commission data)
      const salesCommissionMap = new Map();
      existingCommissions.forEach(commission => {
        if (commission.saleIds && Array.isArray(commission.saleIds)) {
          commission.saleIds.forEach(sale => {
            if (sale && sale._id) {
              salesCommissionMap.set(sale._id.toString(), {
                hasCommission: true,
                paidDate: commission.date,
                commissionAmount: commission.amount
              });
            }
          });
        }
      });

      // Add commission status to sales data and filter only completed sales
      const salesWithCommissionStatus = salesData
        .filter(sale => sale.status === 'completed') // Only show completed sales
        .map(sale => {
          const commissionData = salesCommissionMap.get(sale._id.toString());
          return {
            ...sale,
            hasCommission: commissionData ? commissionData.hasCommission : false,
            commissionPaidDate: commissionData ? commissionData.paidDate : null,
            commissionAmount: commissionData ? commissionData.commissionAmount : null,
          };
        });
      
      console.log("Sales Data with Commission Status:", salesWithCommissionStatus);
      setCommissions(salesWithCommissionStatus);
      setSummary(response?.data?.summary || {
        totalCommissions: 0,
        totalCommissionPaid: "0.00",
      });

      // Fetch commission summary for the selected employee and month
      await fetchCommissionSummary(employeeId, month);

      setPaginationInfo({
        current: page,
        pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching sales:", err);
      message.error("Failed to load sales data.");
    } finally {
      setLoading(false);
    }
  };

  const getEmployees = async () => {
    try {
      const response = await API.get("/employee");
      setEmployees(response?.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      message.error("Failed to load employees.");
    }
  };

  useEffect(() => {
    getSales(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize]);

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Sale Invoice",
      render: (_, sale) => sale?.invoiceId,
      key: "saleInvoiceId",
      filterDropdown: () => (
        <Input
          placeholder="Search Sale Invoice"
          onChange={(e) => handleSearchChange("search", e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Sale Date",
      key: "orderDate",
      render: (_, sale) => dayjs(sale?.orderDate).format("DD-MM-YYYY"),
    },
    {
      title: "Customer",
      key: "customerName",
      render: (_, sale) => sale?.customerId?.name || "N/A",
    },
    {
      title: "Total Amount (₹)",
      key: "totalAmount",
      render: (_, sale) =>
        `₹ ${new Intl.NumberFormat().format(sale?.totalAmount || 0)}`,
    },
    {
      title: "Commission Status",
      key: "commissionStatus",
      width: 150,
      render: (_, sale) => {
        if (sale?.hasCommission) {
          return (
            <div>
              <span style={{ color: "green", fontSize: "16px", fontWeight: "bold", display: "block" }}>
                ✓ Paid
              </span>
              {sale.commissionPaidDate && (
                <span style={{ fontSize: "12px", color: "#666", display: "block" }}>
                  {dayjs(sale.commissionPaidDate).format("DD-MM-YYYY")}
                </span>
              )}
            </div>
          );
        }
        return (
          <span style={{ color: "gray", fontSize: "16px" }}>
            ○ Not Paid
          </span>
        );
      },
    },
    {
      title: "Store",
      key: "storeName",
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", color: '#1591EA', fontSize: "12px" }}>
            {sale?.storeId?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "Sale Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        if (status === "pending") color = "orange";
        else if (status === "completed") color = "green";
        else if (status === "cancelled") color = "red";
        return (
          <span style={{ color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <div
        style={{
          fontWeight: "bold",
          fontSize: "20px",
          textAlign: "center",
          marginBottom: "10px",
          color: "#1890ff",
        }}
      >
        Employee Sales Report
      </div>
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 10px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          marginBottom: "10px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "500" }}>
          Filter by Employee and Month:
        </div>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="Select Employee"
            value={filters.employeeId}
            onChange={(value) => handleSearchChange("employeeId", value)}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {employees.map((employee) => (
              <Option key={employee._id} value={employee._id}>
                {employee.name}
              </Option>
            ))}
          </Select>
          <DatePicker
            picker="month"
            style={{ width: 200 }}
            value={filters.month ? dayjs(filters.month) : null}
            onChange={handleMonthChange}
            format="YYYY-MM"
            placeholder="Select Month"
          />
          <Button
            type="primary"
            icon={<WalletOutlined />}
            onClick={() => setDrawerOpen(true)}
            disabled={!filters.employeeId || !filters.month}
          >
            Pay Commission
          </Button>
        </Space>
      </Space>
      <div
        style={{
          padding: "10px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          marginBottom: "10px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "10px" }}>
          Summary
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div>
            <strong>Sales Count:</strong> {summary.salesCount || 0}
          </div>
          <div>
            <strong>Total Sales Amount:</strong> ₹ {new Intl.NumberFormat().format(summary.totalAmount || 0)}
          </div>
          <div>
            <strong>Commission Paid Amount:</strong> ₹ {new Intl.NumberFormat().format(commissionSummary.totalCommissionPaid || 0)}
          </div>
          <div>
            <strong>Commission Legend:</strong> 
            <span style={{ color: "green", fontSize: "16px", marginLeft: "8px" }}>✓ Paid</span>
            <span style={{ color: "gray", fontSize: "14px", marginLeft: "16px" }}>○ Not Paid</span>
          </div>
        </div>
      </div>
      <CustomTable
        columns={columns}
        dataSource={commissions}
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          });
        }}
        loading={loading}
        style={{ padding: "1px", width: "100%" }}
        total={paginationInfo.total}
      />
        <EmployeeCommissionDrawer
          open={drawerOpen}
          setOpen={setDrawerOpen}
          employeeId={filters.employeeId}
          month={filters.month}
          salesSummary={summary}
          onCommissionCreated={() => {
            // Refresh commission summary when new commission is created
            if (filters.employeeId && filters.month) {
              fetchCommissionSummary(filters.employeeId, filters.month);
            }
          }}
        />
    </div>
  );
}